import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

import { SnIncidentsAPI } from '@lib/api/services';
import { SnFormModule } from '@lib/form';
import { SnGoogleGeocoderResult } from '@lib/geo/google/geocoder';
import { TranslationModule, t } from '@lib/translation';
import { SnFormFieldConfig } from 'libs/form/src/lib/types/form-field-config.type';

import { SnIncidentFieldsGenerator } from '../../services/incident-fields-generator.service';

@Component({
  selector: 'sn-add-new-incident',
  templateUrl: './add-new-incident.component.html',
  styleUrls: ['./add-new-incident.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    TranslationModule,
    SnFormModule,
  ],
})
export class AddNewIncidentComponent implements OnDestroy, OnInit {
  public readonly form = new UntypedFormGroup({});
  public formModel: any = {};
  public readonly formFields: SnFormFieldConfig[];

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _vaultIncidentFieldsGeneratorService: SnIncidentFieldsGenerator,
    private readonly _matDialogRef: MatDialogRef<AddNewIncidentComponent>,
    private readonly _incidentsApi: SnIncidentsAPI,
  ) {
    this.formFields = [
      this._vaultIncidentFieldsGeneratorService.getCaseNumberField({
        templateOptions: {
          required: true,
          label: t('common.caseNumber'),
        },
      }),
      this._vaultIncidentFieldsGeneratorService.getDescriptionField({
        templateOptions: {
          label: t('common.description'),
        },
      }),
      this._vaultIncidentFieldsGeneratorService.getAddressField({
        templateOptions: {
          label: t('common.address'),
          placeholder: t('common.enterAddress'),
          change: (field, place) => this._updateGeometry(place),
        },
      }),
      this._vaultIncidentFieldsGeneratorService.getCityField({
        templateOptions: {
            label: t('common.city'),
            placeholder: t('incidentManagement.dialogs.createNewIncident.enterCity'),
        },
      }),
      {
        fieldGroupClassName: 'fs-row',
        fieldGroup: [
          this._vaultIncidentFieldsGeneratorService.getStateField({
            className: 'state-col',
            templateOptions: {
              label: t('common.state'),
              placeholder: t('incidentManagement.dialogs.createNewIncident.enterState'),
            },
          }),
          this._vaultIncidentFieldsGeneratorService.getZipField({
            className: 'zip-col',
            templateOptions: {
              label: t('incidentManagement.dialogs.createNewIncident.zip'),
              placeholder: t('incidentManagement.dialogs.createNewIncident.enterZip'),
            },
          }),
        ],
      },
    ];
  }

  public ngOnInit(): void {}

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public submit(): void {
    this._incidentsApi
      .createIncident(this.formModel)
      .pipe(takeUntil(this._destroy$))
      .subscribe((res) => {
        this._matDialogRef.close(res);
      });
  }

  private _updateGeometry(place: SnGoogleGeocoderResult): void {
    if (!place.geometry) {
      return;
    }

    this.form.patchValue({
      city: place.addressComponents.city,
      state: place.addressComponents.state,
      zip: place.addressComponents.zip,
    });
    this.formModel.geometry = place.geometry;
  }
}
